import { createFileRoute } from "@tanstack/react-router";
import { useId, useMemo, useState } from "react";
import { Copy, Check, Download, ExternalLink } from "lucide-react";
import { ToolShell } from "@/components/ToolShell";
import { buildSeo } from "@/lib/seo";
import { TEMPLATES, pruneUndefined, type Field } from "@/lib/schema-templates";

export const Route = createFileRoute("/tools/schema-generator")({
  head: () =>
    buildSeo({
      title: "Schema Markup Generator — Free JSON-LD Generator (10 Types)",
      description:
        "Free JSON-LD schema markup generator. Generate valid Article, FAQ, HowTo, LocalBusiness, Product, Person, Organization, Service, Review and Breadcrumb schema in seconds.",
      path: "/tools/schema-generator",
      ogEyebrow: "Schema Generator",
      keywords: "schema generator, json-ld generator, faq schema, howto schema, localbusiness schema, product schema",
    }),
  component: SchemaGenerator,
});

function SchemaGenerator() {
  const [activeId, setActiveId] = useState(TEMPLATES[0].id);
  const [valuesByTemplate, setValuesByTemplate] = useState<Record<string, Record<string, string>>>(() => {
    const init: Record<string, Record<string, string>> = {};
    for (const t of TEMPLATES) {
      init[t.id] = {};
      for (const f of t.fields) init[t.id][f.key] = f.defaultValue ?? "";
    }
    return init;
  });
  const [copied, setCopied] = useState(false);

  const template = TEMPLATES.find((t) => t.id === activeId)!;
  const values = valuesByTemplate[activeId];

  const setValue = (key: string, val: string) =>
    setValuesByTemplate((prev) => ({ ...prev, [activeId]: { ...prev[activeId], [key]: val } }));

  const json = useMemo(() => {
    try {
      return JSON.stringify(pruneUndefined(template.build(values)), null, 2);
    } catch (e) {
      return `// Error building schema: ${(e as Error).message}`;
    }
  }, [template, values]);

  const fullSnippet = `<script type="application/ld+json">\n${json}\n</script>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };

  const download = () => {
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${template.id}-schema.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ToolShell
      eyebrow="Schema Generator"
      title={<>Free JSON-LD <span className="text-gradient">schema generator.</span></>}
      subtitle="Pick a schema type, fill the fields, copy the JSON-LD into your <head>. Ten common types for articles, FAQs, local businesses, products, people and more."
      toolName="Schema Markup Generator"
      toolPath="/tools/schema-generator"
      description="Free JSON-LD schema markup generator covering 10 of the most-used schema types."
      howToSteps={[
        { name: "Pick the schema type", text: "Choose from 10 templates — from Article and FAQ to Person and Service. Each is pre-filled with a working example." },
        { name: "Fill the fields", text: "Edit the form fields. Empty fields are automatically pruned from the output." },
        { name: "Copy the JSON-LD", text: "Click Copy to grab the full <script type=\"application/ld+json\"> snippet ready to paste into your HTML <head>." },
        { name: "Validate it", text: "Use the buttons to open Google Rich Results Test or Schema.org Validator with your URL." },
      ]}
      faqs={[
        {
          q: "Which schema types are most important for SEO?",
          a: "BreadcrumbList and Organization for every site. Article/BlogPosting for blog content. FAQPage for any page with Q&A. LocalBusiness for brick-and-mortar. Product + Review for ecommerce. Person for author E-E-A-T. HowTo for tutorials.",
        },
        {
          q: "Does this generator validate my output?",
          a: "It produces structurally valid JSON-LD that follows Schema.org syntax. For final validation, use the linked Google Rich Results Test (which checks rich-result eligibility) and the Schema.org Validator (which checks vocabulary correctness).",
        },
        {
          q: "Where do I paste the generated code?",
          a: "Inside the <head> of your HTML page, or anywhere in the <body>. Most CMSs (WordPress, Webflow, Shopify) have a custom-code section for head injection. In Next.js / TanStack use a head() function.",
        },
        {
          q: "Why does Person + Organization schema matter for AEO?",
          a: "AI engines use entity signals to decide whom to cite. Person schema with sameAs links to LinkedIn, Wikidata, and X tells engines you're a real, verifiable expert. It is a low-effort way to make your identity clearer to search and AI systems.",
        },
      ]}
      ctaTitle="Want this implemented across your whole site?"
      ctaHref="/services/aeo-geo"
      ctaLabel="See AEO services"
    >
      <div className="flex flex-wrap gap-2 mb-8">
        {TEMPLATES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveId(t.id)}
            className={`rounded-full px-4 py-2 text-xs font-medium border transition ${
              activeId === t.id
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">{template.description}</p>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="min-w-0 space-y-4">
          {template.fields.map((f) => (
            <FieldInput key={f.key} field={f} value={values[f.key] ?? ""} onChange={(v) => setValue(f.key, v)} />
          ))}
        </div>

        <div className="min-w-0">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.2em] text-primary">Generated JSON-LD</div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copy}
                className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-secondary transition"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
              </button>
              <button
                type="button"
                onClick={download}
                className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-secondary transition"
              >
                <Download size={12} /> .json
              </button>
            </div>
          </div>
          <pre className="rounded-2xl border border-border bg-card p-5 text-xs leading-relaxed overflow-auto max-h-[640px]">
            <code>{fullSnippet}</code>
          </pre>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href="https://search.google.com/test/rich-results"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <ExternalLink size={12} /> Google Rich Results Test
            </a>
            <a
              href="https://validator.schema.org/"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <ExternalLink size={12} /> Schema.org Validator
            </a>
          </div>
        </div>
      </div>
    </ToolShell>
  );
}

function FieldInput({ field, value, onChange }: { field: Field; value: string; onChange: (v: string) => void }) {
  const base =
    "mt-2 w-full min-w-0 rounded-xl border border-border bg-card px-4 py-2.5 text-sm focus:outline-none focus:border-primary/60";
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className="text-xs uppercase tracking-[0.18em] text-primary/90 flex items-center gap-2">
        {field.label}
        {field.required && <span className="text-destructive" aria-hidden="true">*</span>}
      </label>
      {field.type === "textarea" || field.type === "list" || field.type === "kvlist" ? (
        <textarea
          id={id}
          aria-required={field.required || undefined}
          aria-describedby={field.hint ? hintId : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={field.type === "kvlist" ? 5 : 3}
          className={base}
          placeholder={field.placeholder}
        />
      ) : (
        <input
          id={id}
          aria-required={field.required || undefined}
          aria-describedby={field.hint ? hintId : undefined}
          type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={base}
          placeholder={field.placeholder}
        />
      )}
      {field.hint && <p id={hintId} className="text-xs text-muted-foreground mt-1">{field.hint}</p>}
    </div>
  );
}